import {
    Input,
    Textarea,
    MessageBarBody,
    MessageBarTitle
} from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ErrorMessageComponent } from '../../../Error/ErrorMessageComponent';
import { ContractBranch } from '../../../../models/Entities/Contracts/ContractBranch';
import { NewContractBranch } from '../../../../models/Entities/Contracts/NewContractBranch';
import { Protocol } from '../../../../models/Entities/Protocols/Protocol';
import ProtocolSelectorComponent from '../../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent';
import { GuideDrawer, GuideDrawerForm } from '../../../../theme/components/GuideDrawer';
import { GuideField } from '../../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';

// component functions
import * as ComponentFunctions from './BranchForm.Functions';

////i18Next
import { useTranslation } from 'react-i18next';

interface FormValidation {
    success: boolean;
    errors: string[];
}

export const BranchFormComponent = (props: ComponentFunctions.Props) => {

    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<
        FormValidation | undefined
    >();

    const [branchName, setBranchName] = useState<string>();
    const [branchDescription, setBranchDescription] = useState<string>();
    const [selectedProtocols, setSelectedProtocols] = useState<Protocol[] | undefined>([]);

    //useTranslation()
    const [t] = useTranslation();

    useEffect(() => {
        if (props.Contract === undefined)
            props.GetContractById(props.ContractId);
    }, [props.ContractId]);

    useEffect(() => {
        props.ContractBranchId && props.GetContractBranchById(props.ContractBranchId);
    }, [props.Contract !== undefined && props.ContractBranchId]);


    useEffect(() => {
        setBranchName(props.Branch?.branchName);
        setBranchDescription(props.Branch?.branchDescription);
    }, [props.Branch]);

    useEffect(() => {
        if (!props.Branch?.protocolIdRelatedToBranch) {
            setSelectedProtocols([]);
            return;
        }

        const selectedProtocol = (props.Protocols || []).find(
            (protocol: Protocol) => protocol.id === props.Branch?.protocolIdRelatedToBranch
        );

        setSelectedProtocols(selectedProtocol ? [selectedProtocol] : []);
    }, [props.Branch?.protocolIdRelatedToBranch, props.Protocols]);

    // save control
    useEffect(() => {
        if (submittedForm && (props.isAddedSuccessfully || props.Branch?.isUpdatedSuccessfully)) {
            setSubmittedForm(false);
            ResetForm();
            props.dismissPanel();
        }
    }, [submittedForm, props.isAddedSuccessfully, props.Branch?.isUpdatedSuccessfully]);


    const ResetForm = () => {
        setSubmittedForm(false);
        setBranchName(undefined);
        setBranchDescription(undefined);
        setSelectedProtocols([]);
    };

    // Events
    const onChangeBranchNameTextFieldValue = (
        event: React.ChangeEvent<HTMLInputElement>,
        data: { value: string }
    ) => {
        setBranchName(data.value);
    };

    const onChangeBranchDescriptionTextFieldValue = (
        event: React.ChangeEvent<HTMLTextAreaElement>,
        data: { value: string }
    ) => {
        setBranchDescription(data.value);
    };

    const HandleSaveForm = () => {
        setSubmittedForm(true);
        if (ValidateForm()) {
            let CurrentValues: ContractBranch | NewContractBranch;
            if (props.Branch) {
                CurrentValues = {
                    id: props.Branch.id,
                    contractId: props.ContractId,
                    contractAddendumId: props.ContractAddendumId,
                    branchName: branchName!,
                    branchDescription: branchDescription!,
                    protocolIdRelatedToBranch: selectedProtocols?.[0]?.id
                };
                props.UpdateContractBranch(CurrentValues as ContractBranch);
            } else {
                CurrentValues = {
                    contractId: props.ContractId,
                    contractAddendumId: props.ContractAddendumId,
                    branchName: branchName!,
                    branchDescription: branchDescription!,
                    protocolIdRelatedToBranch: selectedProtocols?.[0]?.id
                };
                props.CreateContractBranch(CurrentValues as NewContractBranch);
            }
        }
    };

    const HandleCancelForm = () => {
        ResetForm();
        props.dismissPanel();
    };

    const isSaving = !!(props.isAddingNewOne || props.Branch?.isUpdating);

    const formFields = (
        <GuideDrawerForm onSubmit={HandleSaveForm} testId="branch-form">
            <GuideField
                label={t('branchForm.formFields.branchName')}
                required
                error={
                    submittedForm && !branchName
                        ? t('branchForm.formFields.branchNameRequired')
                        : undefined
                }
            >
                <Input
                    value={branchName || ''}
                    onChange={onChangeBranchNameTextFieldValue}
                />
            </GuideField>
            <GuideField
                label={t('branchForm.formFields.branchDescription')}
            >
                <Textarea
                    value={branchDescription || ''}
                    resize="vertical"
                    onChange={onChangeBranchDescriptionTextFieldValue}
                />
            </GuideField>
            <GuideField
                label={t('branchForm.formFields.protocolRelatedToBranch', 'Protocolo relacionado')}
            >
                <ProtocolSelectorComponent
                    selectionLimit={1}
                    selectedProtocols={selectedProtocols}
                    onChangeSelection={(protocolsSelected: Protocol[] | undefined) => setSelectedProtocols(protocolsSelected || [])}
                />
            </GuideField>
        </GuideDrawerForm>
    );

    // form validation

    const ValidateForm = (): boolean => {
        let validationResult: boolean = true;

        let FormValidation: FormValidation = { success: true, errors: [] };
        setFormValidationStatus(FormValidation);

        if (!branchName || branchName.trim() === '') {
            FormValidation.errors.push(t('branchForm.formFields.branchNameRequired'));
            validationResult = false;
        }

        FormValidation.success = validationResult;
        setFormValidationStatus(FormValidation);
        return validationResult;
    };

    return (
        <GuideDrawer
            open={props.isOpen}
            size="medium"
            eyebrow={props.Contract?.contractNumber || undefined}
            title={t('branchForm.headerText.branchInformation')}
            onDismiss={HandleCancelForm}
            testId="branch-form-drawer"
            secondaryAction={{ label: t('branchForm.footer.cancelButton'), onClick: HandleCancelForm }}
            primaryAction={{
                label: isSaving ? t('branchForm.footer.saving') : t('branchForm.footer.saveButton'),
                onClick: HandleSaveForm,
                loading: isSaving
            }}
        >
            {submittedForm && !FormValidationStatus?.success && (
                <GuideMessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>{t('branchForm.messageBar.reviewInformation')}</MessageBarTitle>
                        <ul>
                            {FormValidationStatus?.errors.map(
                                (Error: string, index: number) => (
                                    <li key={index}>{Error}</li>
                                )
                            )}
                        </ul>
                    </MessageBarBody>
                </GuideMessageBar>
            )}
            {props.failOnAdding &&
                props.error && (
                    <ErrorMessageComponent Error={props.error} />
                )}
            {formFields}
        </GuideDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(BranchFormComponent as any);
